import { useState } from 'react';
import {
  AVATAR_COLORS,
  AVATARS,
  MAX_PLAYERS,
  MIN_PLAYERS,
  MISSIONS,
  MOODS,
  type AvatarColor,
  type AvatarId,
  type ClientSnapshot,
  type Mood
} from '@crew/shared';
import type { Crew } from '../net.js';
import { Avatar } from './Avatar.js';
import { MissionIcon, missionKind } from './Art.js';

export function Lobby({
  crew,
  snapshot,
  onOpenManual
}: {
  crew: Crew;
  snapshot: ClientSnapshot;
  onOpenManual: () => void;
}) {
  const { room } = snapshot;
  const [copied, setCopied] = useState<'code' | 'link' | null>(null);
  const [sort, setSort] = useState<'difficulty' | 'number'>('difficulty');
  const isHost = room.hostId === crew.playerId;
  const enough = room.players.length >= MIN_PLAYERS;
  const me = room.players.find((p) => p.id === crew.playerId);
  const mission = MISSIONS.find((m) => m.id === room.missionId) ?? MISSIONS[0];

  const DIFFICULTY_LABEL = ['', 'Training', 'Routine', 'Demanding', 'Hazardous', 'Brutal'];
  const groups =
    sort === 'number'
      ? [{ label: 'All missions', missions: [...MISSIONS] }]
      : [1, 2, 3, 4, 5]
          .map((d) => ({
            label: DIFFICULTY_LABEL[d] as string,
            missions: MISSIONS.filter((m) => m.difficulty === d)
          }))
          .filter((g) => g.missions.length > 0);

  const copy = async (what: 'code' | 'link') => {
    const text =
      what === 'code' ? room.code : `${window.location.origin}/?room=${room.code}`;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(what);
      window.setTimeout(() => setCopied(null), 1800);
    } catch {
      /* clipboard blocked - the code is on screen anyway */
    }
  };

  return (
    <div className="lobby">
      <section className="lobby__launch" aria-label="Launch controls">
        <div>
          <strong>Mission {mission?.id}: {mission?.name}</strong>
          <span>{room.players.length} of {MAX_PLAYERS} pilots aboard</span>
        </div>
        {isHost ? (
          <button
            className="btn btn--primary btn--big"
            onClick={() => void crew.start()}
            disabled={!enough}
          >
            {enough ? 'Launch mission' : `Need ${MIN_PLAYERS - room.players.length} more`}
          </button>
        ) : (
          <p className="lobby__waiting">
            Waiting for {room.players.find((p) => p.isHost)?.name} to launch&hellip;
          </p>
        )}
        <button className="btn btn--ghost" onClick={onOpenManual}>How to play</button>
      </section>
      <section className="panel lobby__crew">
        <p className="lobby__prompt">Your friends join with this code</p>
        <div className="roomcode" aria-label={`Room code ${room.code.split('').join(' ')}`}>
          {room.code.split('').map((ch, i) => (
            <span key={i}>{ch}</span>
          ))}
        </div>
        <div className="lobby__share">
          <button className="btn btn--small" onClick={() => void copy('code')}>
            {copied === 'code' ? 'Copied' : 'Copy code'}
          </button>
          <button className="btn btn--small btn--primary" onClick={() => void copy('link')}>
            {copied === 'link' ? 'Link copied' : 'Copy invite link'}
          </button>
        </div>

        <p className="lobby__count">
          {room.players.length} of {MAX_PLAYERS} seats filled
          {enough ? null : `. Need ${MIN_PLAYERS - room.players.length} more`}
        </p>
        <ul className="crew-list">
          {room.players.map((p) => (
            <li key={p.id} className={p.connected ? '' : 'is-away'}>
              <Avatar
                id={p.avatar}
                color={p.avatarColor}
                mood={p.mood}
                size={32}
                className="crew-list__avatar"
              />
              <span className="crew-list__name">
                {p.name}
                {p.id === crew.playerId ? ' (you)' : ''}
              </span>
              <span className="crew-list__tags">
                {p.isHost ? <span className="chip chip--cmd">HOST</span> : null}
                {p.connected ? null : <span className="chip chip--off">AWAY</span>}
              </span>
            </li>
          ))}
          {Array.from({ length: MAX_PLAYERS - room.players.length }).map((_, i) => (
            <li key={`empty-${i}`} className="is-empty">
              <span className="crew-list__avatar" aria-hidden="true">
                +
              </span>
              <span className="crew-list__name">Waiting for a pilot&hellip;</span>
            </li>
          ))}
        </ul>

        {me ? (
          <section className="lobby__look" aria-label="Your pilot">
          <div className="lobby__look-preview">
            <Avatar id={me.avatar} color={me.avatarColor} mood={me.mood} size={92} />
            <span>{me.name}</span>
          </div>          <div className="lobby__look-pickers">
            <div>
              <label id="face-label">Face</label>
              <div className="avatar-grid" role="radiogroup" aria-labelledby="face-label">
                {AVATARS.map((id) => (
                  <button
                    key={id}
                    type="button"
                    role="radio"
                    aria-checked={me.avatar === id}
                    aria-label={id}
                    className={`avatar-pick ${me.avatar === id ? 'is-active' : ''}`}
                    onClick={() => void crew.setAvatar(id, me.avatarColor, me.mood)}
                  >
                    <Avatar id={id as AvatarId} color={me.avatarColor} mood={me.mood} size={44} />
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label id="colour-label">Colour</label>
              <div className="colour-grid" role="radiogroup" aria-labelledby="colour-label">
                {AVATAR_COLORS.map((c) => {
                  const taken = room.players.some(
                    (p) => p.id !== crew.playerId && p.avatarColor === c
                  );
                  return (
                    <button
                      key={c}
                      type="button"
                      role="radio"
                      aria-checked={me.avatarColor === c}
                      aria-label={taken ? `${c}, taken` : c}
                      disabled={taken}
                      title={taken ? 'Another pilot has this colour' : c}
                      className={`colour-pick colour-${c} ${me.avatarColor === c ? 'is-active' : ''} ${
                        taken ? 'is-taken' : ''
                      }`}
                      onClick={() => void crew.setAvatar(me.avatar, c as AvatarColor, me.mood)}
                    />
                  );
                })}
              </div>
            </div>
            <div>
              <label id="mood-label">Expression</label>
              <div className="avatar-grid" role="radiogroup" aria-labelledby="mood-label">
                {MOODS.map((m) => (
                  <button
                    key={m}
                    type="button"
                    role="radio"
                    aria-checked={me.mood === m}
                    aria-label={m}
                    title={m}
                    className={`avatar-pick ${me.mood === m ? 'is-active' : ''}`}
                    onClick={() => void crew.setAvatar(me.avatar, me.avatarColor, m as Mood)}
                  >
                    <Avatar id={me.avatar} color={me.avatarColor} mood={m as Mood} size={44} />
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>
        ) : null}

        <label className="house-rule">
          <input
            type="checkbox"
            checked={room.houseRules.relaxedComms}
            disabled={!isHost}
            onChange={(e) => void crew.setHouseRules({ relaxedComms: e.target.checked })}
          />
          <span>
            <strong>Relaxed comms</strong>
            <small>
              Hint any colour card, not just your highest, lowest or only one. Easier than the
              standard game.
            </small>
          </span>
        </label>

        <label className="house-rule">
          <input
            type="checkbox"
            checked={room.houseRules.hideFlightLog}
            disabled={!isHost}
            onChange={(e) => void crew.setHouseRules({ hideFlightLog: e.target.checked })}
          />
          <span>
            <strong>No flight log</strong>
            <small>
              Hide the running record of what was played. You have to remember the tricks
              yourselves. Harder.
            </small>
          </span>
        </label>
      </section>

      <section className="panel lobby__missions">
        <div className="missions__head">
          <h2>Mission select</h2>
          <div className="missions__sort" role="group" aria-label="Sort missions">
            <button
              className={sort === 'difficulty' ? 'is-active' : ''}
              onClick={() => setSort('difficulty')}
            >
              By difficulty
            </button>
            <button className={sort === 'number' ? 'is-active' : ''} onClick={() => setSort('number')}>
              By number
            </button>
          </div>
        </div>
        <p className="hint">
          {isHost ? 'Pick a mission. Difficulty climbs as you go.' : 'The host chooses the mission.'}
        </p>
        <ul className="mission-list">
          {groups.map(({ label, missions }) => (
            <li key={label} className="mission-group">
              {sort === 'difficulty' ? (
                <p className="mission-group__label">
                  {label}
                  <span className="difficulty">
                    {'\u25CF'.repeat(missions[0]?.difficulty ?? 1)}
                    <span className="difficulty__off">
                      {'\u25CF'.repeat(5 - (missions[0]?.difficulty ?? 1))}
                    </span>
                  </span>
                </p>
              ) : null}
              <ul className="mission-group__list">
                {missions.map((m) => {
                  const cleared = room.clearedMissions.includes(m.id);
                  return (
                    <li key={m.id}>
                      <button
                        type="button"
                        className={`mission ${m.id === room.missionId ? 'is-active' : ''}`}
                        onClick={() => void crew.setMission(m.id)}
                        disabled={!isHost}
                      >
                        <span className="mission__no">{m.id}</span>
                        <span className="mission__emblem">
                          <MissionIcon kind={missionKind(m)} />
                        </span>
                        <span className="mission__body">
                          <strong>{m.name}</strong>
                          <small>{m.brief}</small>
                        </span>
                        <span className="mission__meta">
                          {sort === 'number' ? (
                            <span
                              className="difficulty"
                              aria-label={`difficulty ${m.difficulty} of 5`}
                            >
                              {'\u25CF'.repeat(m.difficulty)}
                              <span className="difficulty__off">
                                {'\u25CF'.repeat(5 - m.difficulty)}
                              </span>
                            </span>
                          ) : null}
                          {cleared ? <span className="chip chip--you">CLEARED</span> : null}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </li>
          ))}
        </ul>
        {mission ? (
          <div className="mission-detail">
            <strong>{mission.name}</strong>
            <p>{mission.brief}</p>
            <ul>
              <li>Tasks: {mission.tasks[3]} / {mission.tasks[4]} / {mission.tasks[5]} for 3 / 4 / 5 players</li>
              {mission.ordered ? (
                <li>
                  Ordered tasks: {mission.ordered === 'all' ? 'every task' : mission.ordered}
                </li>
              ) : null}
              {mission.finalTask ? <li>One task must be completed last</li> : null}
              {mission.rules?.noCommunication ? <li>No hints at all</li> : null}
              {mission.rules?.communicationLockedUntilTrick ? (
                <li>Hints locked until trick {mission.rules.communicationLockedUntilTrick}</li>
              ) : null}
              {mission.rules?.communicationClosesAtTrick ? (
                <li>Hints close after trick {mission.rules.communicationClosesAtTrick - 1}</li>
              ) : null}
              {mission.rules?.commanderWinsFirstTrick ? (
                <li>The commander must win the first trick</li>
              ) : null}
              {mission.rules?.noBackToBackTricks ? (
                <li>Nobody may win two tricks in a row</li>
              ) : null}
            </ul>
          </div>
        ) : null}
      </section>
    </div>
  );
}
